import { useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { getClientFirestore } from "@/api/firebase";
import { useAuthStore } from "@/stores/auth-store";
import { useToastStore } from "@/stores/toast-store";
import { useQueryClient } from "@tanstack/react-query";

export function useRealtimeNotifications() {
  const orgId = useAuthStore((s) => s.orgId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!orgId || !isAuthenticated) return;

    isInitialLoad.current = true;

    const clientDb = getClientFirestore(orgId);
    const q = query(
      collection(clientDb, "inAppNotifications"),
      where("read", "==", false),
      orderBy("createdAt", "desc"),
      limit(20),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          return;
        }

        for (const change of snapshot.docChanges()) {
          if (change.type === "added") {
            const data = change.doc.data();
            addToast({
              type:
                data.severity === "critical"
                  ? "error"
                  : data.severity === "warning"
                    ? "warning"
                    : "info",
              title: data.title ?? "New alert",
              description: data.category
                ? `${(data.category as string).replace(/_/g, " ")} alert`
                : undefined,
              duration: 8000,
            });
          }
        }

        queryClient.invalidateQueries({ queryKey: ["alerts"] });
      },
      (error) => {
        console.error("Realtime notifications error:", error);
      },
    );

    return () => unsubscribe();
  }, [orgId, isAuthenticated, addToast, queryClient]);
}
